import { useId, type ReactNode } from 'react';

interface DetailSectionProps {
  title?: ReactNode;
  description?: ReactNode;
  /** Section-level actions (at most one primary on the whole page). */
  actions?: ReactNode;
  /**
   * `flush` removes the body padding so a table can run edge to edge under a
   * hairline — the table is part of the section, not a box inside it.
   */
  flush?: boolean;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}

/**
 * The one white surface of a detail page section (DESIGN_SYSTEM.md §4 "flat,
 * not nested"). Inside it, group with spacing, dividers and definition lists —
 * never with another bordered or shadowed box.
 */
export default function DetailSection({
  title, description, actions, flush = false, className = '', bodyClassName = '', children,
}: DetailSectionProps) {
  const headingId = useId();
  const hasHeader = !!(title || actions);
  return (
    <section
      aria-labelledby={title ? headingId : undefined}
      className={`bg-surface-0 border border-surface-200 rounded-xl min-w-0 ${className}`}
    >
      {hasHeader && (
        <div className={`flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-4 sm:px-5 pt-4 ${flush ? 'pb-3' : 'pb-1'}`}>
          <div className="min-w-0">
            {title && <h2 id={headingId} className="text-base font-semibold text-surface-900">{title}</h2>}
            {description && <p className="mt-0.5 text-xs text-surface-600 max-w-2xl">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      <div
        className={`${flush ? (hasHeader ? 'border-t border-surface-200' : '') : `px-4 sm:px-5 ${hasHeader ? 'pt-3' : 'pt-4 sm:pt-5'} pb-4 sm:pb-5`} ${bodyClassName}`}
      >
        {children}
      </div>
    </section>
  );
}

/** Label/value pairs in a responsive grid (read-only facts inside a section). */
export function FactGrid({
  items, columns = 2,
}: {
  items: { label: ReactNode; value: ReactNode; ltr?: boolean; wide?: boolean }[];
  columns?: 2 | 3;
}) {
  return (
    <dl className={`grid grid-cols-1 sm:grid-cols-2 ${columns === 3 ? 'lg:grid-cols-3' : ''} gap-x-6 gap-y-4`}>
      {items.map((it, i) => (
        <div key={i} className={`min-w-0 ${it.wide ? 'sm:col-span-2' : ''}`}>
          <dt className="text-xs text-surface-500">{it.label}</dt>
          <dd className="mt-0.5 text-sm text-surface-900 break-words" dir={it.ltr ? 'ltr' : undefined}>{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Label at the start, value at the end, hairline between rows (totals, lifecycle facts). */
export function FactRows({
  items,
}: {
  items: { label: ReactNode; value: ReactNode; strong?: boolean }[];
}) {
  return (
    <dl className="divide-y divide-surface-100">
      {items.map((it, i) => (
        <div key={i} className={`flex items-baseline justify-between gap-3 py-2 first:pt-0 last:pb-0 ${it.strong ? 'text-base font-semibold text-surface-900' : 'text-sm'}`}>
          <dt className={it.strong ? '' : 'text-surface-600'}>{it.label}</dt>
          <dd className={`text-end tabular-nums ${it.strong ? '' : 'text-surface-900'}`}>{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}
